/**
 * AppShell.tsx - Layout principal do shell autenticado do Órbita.
 *
 * Estrutura: Topbar (56px) + [Sidebar (240px/64px) + Main Content]
 * Responsivo: uma única sidebar em md+ (≥768px) que nasce EXPANDIDA (ícone +
 * rótulo, sempre legível sem interação). O usuário pode recolher para o modo só
 * ícones (64px) por um botão dedicado; a preferência é persistida em
 * localStorage. Só no modo recolhido o hover/foco revela os rótulos como
 * overlay temporário. Em telas estreitas (<768px) usa o drawer mobile.
 *
 * Referência: NAVEGACAO.md §3.1, UX.md §2.3
 */

import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import { AppTopbar } from "./AppTopbar";
import { AppSidebar } from "./AppSidebar";

/** Chave de persistência da preferência de sidebar recolhida (localStorage). */
const SIDEBAR_STORAGE_KEY = "orbita.sidebar.recolhida";

/** Lê a preferência salva de forma tolerante a falha (localStorage pode faltar
 *  ou lançar, ex.: SSR, modo privado, storage bloqueado). */
function lerPreferenciaRecolhida(): boolean {
  try {
    return globalThis.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
  } catch {
    // localStorage ausente/bloqueado (SSR, modo privado): default expandida.
    return false;
  }
}

/** Grava a preferência de forma tolerante a falha (best-effort). */
function gravarPreferenciaRecolhida(recolhida: boolean): void {
  try {
    globalThis.localStorage.setItem(SIDEBAR_STORAGE_KEY, recolhida ? "true" : "false");
  } catch {
    // Persistência é best-effort; a UI segue funcionando sem ela.
  }
}

// ---------------------------------------------------------------------------
// Drawer mobile (sheet lateral)
// ---------------------------------------------------------------------------

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  return (
    <>
      {/* Overlay (aria-hidden=true, interação via botão fechar no drawer) */}
      {open && (
        <div
          aria-hidden="true"
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--color-nav-scrim)",
            zIndex: 40,
          }}
        />
      )}

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: "280px",
          background: "var(--color-nav-bg)",
          borderRight: "1px solid var(--color-nav-border)",
          zIndex: 50,
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 250ms ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header do drawer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "56px",
            padding: "0 1rem",
            borderBottom: "1px solid var(--color-nav-border)",
            flexShrink: 0,
          }}
        >
          {/* Logo oficial do Órbita (a MESMA do login e da institucional:
              logo-orbita.png) no header do drawer mobile */}
          <img
            src={`${import.meta.env.BASE_URL}logo-orbita.png`}
            alt="Órbita"
            style={{ height: "26px", width: "auto", objectFit: "contain", display: "block" }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            style={{
              background: "none",
              border: "none",
              /* Icone de fechar sobre navy: bone-dim (~7.6:1); o cinza bruma
                 antigo ficava em ~3.6:1 (divida de contraste resolvida). */
              color: "var(--color-nav-text-muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "4px",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <AppSidebar isDrawer onNavClick={onClose} />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidebar md+ (unificada): expandida por padrão, recolhível com persistência
// ---------------------------------------------------------------------------

interface DesktopSidebarProps {
  /** Preferência do usuário: sidebar recolhida (só ícones). Persistida. */
  recolhida: boolean;
  /** Alterna a preferência recolhida/expandida (grava no localStorage). */
  onToggle: () => void;
}

/**
 * Sidebar única para md+ (≥768px). Nasce EXPANDIDA (240px, ícone + rótulo
 * sempre legível, sem depender de hover). O usuário pode recolher para o modo
 * só ícones (64px) pelo botão de recolher; a escolha fica persistida.
 *
 * Só quando `recolhida === true` o hover/focus-within expande temporariamente
 * como OVERLAY: o slot de 64px permanece no fluxo (o conteúdo não salta) e a
 * camada expandida sobrepõe o main com sombra. No modo expandido o hover não
 * altera nada (a sidebar fica fixa em 240px com os rótulos visíveis).
 */
function DesktopSidebar({ recolhida, onToggle }: DesktopSidebarProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // O hover só expande no modo recolhido; no modo expandido é ignorado.
  const hoverExpandido = recolhida && (hovered || focused);
  // Camada visual: 240px quando expandida ou revelada por hover; 64px recolhida.
  const larguraCamada = !recolhida || hoverExpandido ? "240px" : "64px";
  // Rótulos ocultos só quando recolhida E sem hover ativo.
  const collapsed = recolhida && !hoverExpandido;
  // Slot no fluxo: 240px no modo expandido; 64px no recolhido (a expansão por
  // hover vira overlay absoluto, sem empurrar o conteúdo).
  const larguraSlot = recolhida ? "64px" : "240px";

  return (
    <aside
      aria-label="Navegação lateral"
      className="hidden md:block"
      onMouseEnter={() => { setHovered(true); }}
      onMouseLeave={() => { setHovered(false); }}
      onFocusCapture={() => { setFocused(true); }}
      onBlurCapture={() => { setFocused(false); }}
      style={{
        width: larguraSlot,
        flexShrink: 0,
        position: "relative",
        zIndex: hoverExpandido ? 30 : 1,
        transition: "width 200ms ease",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: larguraCamada,
          background: "var(--color-nav-bg)",
          borderRight: "1px solid var(--color-nav-border)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          boxShadow: hoverExpandido ? "4px 0 24px rgba(0,0,0,0.35)" : "none",
          transition: "width 200ms ease, box-shadow 200ms ease",
        }}
      >
        <div style={{ flex: 1, minHeight: 0 }}>
          <AppSidebar collapsed={collapsed} />
        </div>

        {/* Botão de recolher/expandir (persiste a escolha do usuário). */}
        <div
          style={{
            flexShrink: 0,
            borderTop: "1px solid var(--color-nav-border)",
            padding: "0.5rem",
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onToggle}
            aria-label={recolhida ? "Expandir menu" : "Recolher menu"}
            aria-pressed={recolhida}
            title={recolhida ? "Expandir menu" : "Recolher menu"}
            style={{
              background: "none",
              border: "none",
              color: "var(--color-nav-item-inactive)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "4px",
              transition: "background 150ms, color 150ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-nav-hover)";
              e.currentTarget.style.color = "var(--color-nav-hover-text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "";
              e.currentTarget.style.color = "var(--color-nav-item-inactive)";
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{
                transform: recolhida ? "rotate(180deg)" : "none",
                transition: "transform 200ms ease",
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// AppShell principal
// ---------------------------------------------------------------------------

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Preferência inicial lida do localStorage (default: expandida = labels visíveis).
  const [recolhida, setRecolhida] = useState<boolean>(() => lerPreferenciaRecolhida());

  // Mantém o localStorage em sincronia com a escolha do usuário.
  useEffect(() => {
    gravarPreferenciaRecolhida(recolhida);
  }, [recolhida]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "var(--color-bg-base)",
      }}
    >
      {/* Topbar */}
      <AppTopbar onMenuClick={() => { setDrawerOpen(true); }} />

      {/* Body: sidebar + conteúdo */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar única md+ (expandida por padrão, recolhível com persistência) */}
        <DesktopSidebar
          recolhida={recolhida}
          onToggle={() => { setRecolhida((v) => !v); }}
        />

        {/* Conteúdo principal */}
        <main
          id="main-content"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* Drawer mobile */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); }}
      />
    </div>
  );
}
